/* eslint-disable prettier/prettier */
import { useState, useContext, useEffect, useRef, useCallback, Suspense, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
	Grid,
	Checkbox,
	Button,
	Paper,
	TextField,
	MenuItem,
	Select,
	makeStyles,
	withStyles,
	useTheme,
	Card,
	CheckboxProps
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { trackPromise } from "react-promise-tracker";
import { Validation } from "../../../../../@types/validation.types";
import { SnackbarProps } from "../../../../../@types/snackbarAPI.types";
import { AuthContext, UIContext } from "../../../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import { api } from "../../../../../utils/api";
import { LazySnackbarAPI } from "../../../../shared";
import { useEmployerRoute, useQuery } from "../../../../../hooks";

import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import SaveIcon from "@material-ui/icons/Save";
import PhotoCamera from "@material-ui/icons/PhotoCamera";
import sampleImage from "./sample.jpg";

import "./editHealthLinks.css";
import generateImageDialogLink from "../../../../../utils/commonFunctions/generateImageDialogLink";
import useRouteParam from "../../../../../hooks/useRouteParam";
import { groups } from "d3";
import { GROUP_NUMBER } from "../../../../../constants/groups";
import { Label } from "recharts";

// eslint-disable-next-line arrow-parens
const useStyles = makeStyles((theme) => ({
	margin: {
		margin: theme.spacing(1)
	},
	large: {
		width: 150,
		height: 150
	},
	paperFullWidth: {
		overflowY: "visible"
	},
	dialogContentRoot: {
		overflowY: "visible"
	}
}));

const EditHealthLinks = () => {
	const classes = useStyles();
	const navigate = useNavigate();
	const theme = useTheme();
	const query = useQuery();
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");
	const { user } = useContext(AuthContext);
	const [assignedGroups, setAssignedGroups] = useState([]);
	const [healthLink, setHealthLink] = useState<any>({});

	const [groupOptions, setGroupOptions] = useState<any>([]);
	const [groupName, setGroupName] = useState<any>();
	const [isValidHealthLinkId, setIsValidHealthLinkId] = useState<boolean | null>(null);
	const [hasHealthLinkChanged, setHasHealthLinkChanged] = useState(false);
	const [isNewHealthLink, setIsNewHealthLink] = useState<boolean | null>(null);
	const [validation, setValidation] = useState<Validation>();
	const _validation = useRef<Validation>();
	const { setDashboardHeader } = useContext(UIContext);
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const categoryOptions = useRef([
		{
			value: "Other",
			label: "Other"
		},
		{
			value: "PBM",
			label: "PBM"
		},
		{
			value: "PPO",
			label: "PPO"
		},
		{
			value: "UtilRev/CaseMgmt",
			label: "UtilRev/CaseMgmt"
		}
	]);
	const GreenCheckbox = withStyles({
		root: {
			color: green[400],
			"&$checked": {
				color: green[600]
			}
		},
		checked: {}
	})((props: CheckboxProps) => <Checkbox color="default" {...props} checked={healthLink?.is_custom_image_enabled} />);

	const handleLinkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		setHealthLink(
			Object.assign({}, healthLink, {
				[name]: value
			})
		);
		setHasHealthLinkChanged(true);
	};
	const handleChange = (
		event: React.ChangeEvent<{
			name?: string | undefined;
			value: unknown;
		}>,
		child: ReactNode
	) => {
		const {
			target: { value }
		} = event;
		setHealthLink(Object.assign({}, healthLink, { category: value }));
		setHasHealthLinkChanged(true);
	};
	const handleChangeGroup = (
		event: React.ChangeEvent<{
			name?: any;
			value: unknown;
		}>,
		child: ReactNode
	) => {
		const {
			target: { value }
		} = event;
		// console.log("first", value);
		setHealthLink(Object.assign({}, healthLink, { group_number: value }));
		setHasHealthLinkChanged(true);
	};

	const handleCustomImageCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = event.target;
		setHealthLink(
			Object.assign({}, healthLink, {
				[name]: checked
			})
		);
		setHasHealthLinkChanged(true);
	};

	// For Uploading the Custom Image
	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { files } = event.target;
		if (files && files[0]) {
			setHealthLink(
				Object.assign({}, healthLink, {
					image: files[0]
				})
			);
			setHasHealthLinkChanged(true);
		}
	};

	const getHealthLink = useCallback(async (id: string) => {
		const _healthLink = await api.healthLinks.getHealthLinkById(id);
		// console.log("_healthLink", _healthLink);
		if (_healthLink) {
			setHealthLink(Object.assign({}, _healthLink));
			setIsValidHealthLinkId(true);
		}
	}, []);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleValidation = async () => {
		const { image, text, description, URL, category, is_custom_image_enabled, group_number } = healthLink;

		_validation.current = {
			status: "invalid"
		};
		let flag = true;

		if (!group_number) {
			_validation.current["group_number"] = "group_number is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (text.length === 0) {
			_validation.current["text"] = "Display Text is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (category.length === 0) {
			_validation.current["category"] = "Category is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (URL.length === 0) {
			_validation.current["URL"] = "URL is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (description.length === 0) {
			_validation.current["description"] = "Description is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (is_custom_image_enabled === true) {
			if (image === null) {
				_validation.current["image"] = "Image is required";
				_validation.current["status"] = "invalid";
				flag = false;
			}
		}

		if (is_custom_image_enabled === true) {
			if (image === "") {
				_validation.current["image"] = "Image is required";
				_validation.current["status"] = "invalid";
				flag = false;
			}
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}

		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	};

	const handleNavigate = useCallback(() => {
		// const pathname = "/settings/blogs";
		// navigate(pathname);
		let pathname;
		if (isEmployerRoute === true && queryGroupNumber !== null) {
			pathname = `/employer/settings/blogs?gn=${queryGroupNumber}`;
		} else {
			pathname = "/settings/blogs";
		}
		navigate(pathname);
	}, [isEmployerRoute, navigate, queryGroupNumber]);

	const handleSaveClick = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			const status = await handleValidation();
			if (status === "valid") {
				event.preventDefault();
				if (!hasHealthLinkChanged) {
					handleNavigate();
					return;
				}
				const formData = new FormData();
				formData.append("image", healthLink.image);
				formData.append("blog", JSON.stringify(healthLink));
				let _healthLink;
				if (healthLink._id) {
					_healthLink = await trackPromise(api.healthLinks.editHealthLink(formData));
				} else {
					_healthLink = await trackPromise(api.healthLinks.addHealthLink(formData));
				}
				if (_healthLink) {
					setHealthLink(Object.assign({}, _healthLink));
					alert("Successfully saved health link");
					handleNavigate();
				}
			}
		},
		[handleNavigate, handleValidation, hasHealthLinkChanged, healthLink]
	);

	const handleAssignClick = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			const status = await handleValidation();
			if (status === "valid") {
				event.preventDefault();
				const formData = new FormData();
				formData.append("image", healthLink.image);
				formData.append("blog", JSON.stringify(healthLink));
				let _healthLink: any;
				if (healthLink._id) {
					_healthLink = await trackPromise(api.healthLinks.editBlog(formData));
				} else {
					_healthLink = await trackPromise(api.healthLinks.addBlog(formData));
				}
				if (_healthLink) {
					setHealthLink(Object.assign({}, _healthLink));

					setSnackbarAPICallProps(
						Object.assign({}, snackbarAPICallProps, {
							open: true,
							message: "Successfully saved health information",
							severity: "success"
						})
					);

					setTimeout(() => {
						const pathname =
							isEmployerRoute === true && queryGroupNumber !== null
								? `/employer/settings/blog/assign-groups?id=${_healthLink._id}&&gn=${queryGroupNumber}`
								: `/settings/blog/assign-groups?id=${_healthLink._id}`;
						navigate(pathname);
						// navigate(`/settings/blog/assign-groups?id=${_healthLink._id}`);
					}, 3000);
				}
			}
		},
		[handleValidation, healthLink, navigate, snackbarAPICallProps, queryGroupNumber]
	);

	const getAssignGroups = useCallback(async () => {
		const params = {
			user_id: user?._id
		};
		const response = await api.assignGroupsAndLocation.getAssignedGroupsAndLocations(params);

		if (response) {
			const _assignedGroups: any[] = [];
			response.groupsAndLocations.forEach((group: any) => {
				if (group.assigned_group_info) {
					_assignedGroups.push(group);
				}
			});

			setAssignedGroups(Object.assign([], _assignedGroups));
		}
	}, [user?._id]);

	// const assignedGroup = assignedGroups.find((group: any) => group.group_number === queryGroupNumber);

	useEffect(() => {
		if (assignedGroups.length > 0) {
			const assignedGroup = assignedGroups.map((group: any) => ({
				value: `${group.group_number}`,
				label: `${group.name}-${group.group_number}`
			}));
			const assignGroupName = assignedGroups.find((group: any) => group.group_number === queryGroupNumber);
			if (assignGroupName) {
				const { name, group_number } = assignGroupName;
				setGroupName([`${name}-${group_number}`, group_number]);
				setHealthLink(Object.assign({}, healthLink, { group_number: group_number }));
				// debugger;
				// console.log("name&number", name, group_number);
			}
			setGroupOptions(assignedGroup);
		}
	}, [assignedGroups, queryGroupNumber]);
	const filterGroupOption = groupOptions.filter((groupOption: any) => groupOption.label === 220);
	// console.log("filterGroupOption", filterGroupOption);
	// console.log("group", groupOptions, assignedGroups);

	useEffect(() => {
		// setDashboardHeader(ADMIN_DASHBOARD_HEADER.edit_blogs);
		if (query) {
			const _isNewHealthLink = query.get("new") === "true" ? true : query.get("new") === "false" ? false : null;
			setDashboardHeader(_isNewHealthLink ? ADMIN_DASHBOARD_HEADER.add_blogs : ADMIN_DASHBOARD_HEADER.edit_blogs);
			if (!_isNewHealthLink) {
				getHealthLink(query.get("id") as string);
			} else {
				setHealthLink(
					Object.assign(
						{},
						{
							group_number: null,
							category: "",
							text: "",
							URL: "",
							description: "",
							image: null,
							is_custom_image_enabled: false
						}
					)
				);
				setIsValidHealthLinkId(true);
			}
			setIsNewHealthLink(_isNewHealthLink);
		}
		getAssignGroups();
	}, [getAssignGroups, getHealthLink, query, setDashboardHeader]);

	// console.log("====assignGroup", assignedGroups);
	// console.log("====groupName", groupName);

	// console.log("healthLink", isValidHealthLinkId);

	return (
		<div className="edit-health-links" id="edit-health-links">
			<Suspense fallback={<div />}>
				<LazySnackbarAPI snackbarProps={snackbarAPICallProps} />
			</Suspense>
			{isValidHealthLinkId ? (
				<>
					<div className="group-assign-action-button-container" id="group-assign-action-button-container">
						<Button className="button-green" onClick={handleAssignClick} variant="contained">
							<span className="button-label-with-icon">Assign Locations</span>
							<span>
								<PeopleAltIcon className="button-icon" />
							</span>
						</Button>
						<Button className="button-black" onClick={handleSaveClick} variant="contained">
							<span className="button-label-with-icon">Save</span>
							<span>
								<SaveIcon className="button-icon" />
							</span>
						</Button>
					</div>
					<Grid container spacing={1} className="edit-health-links-container">
						<div className="title">Blog Information</div>
						<Paper className="paper-container" elevation={3} variant="outlined">
							<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
								<Grid container spacing={1}>
									<Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
										<div className="label-text" id="label-text">
											Group
										</div>
									</Grid>
									<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
										<Select
											id="group-select"
											name="group"
											value={
												queryGroupNumber
													? groupName
														? groupName[1]
														: null
													: healthLink && healthLink.group_number
											}
											label={
												queryGroupNumber
													? groupName
														? groupName[0]
														: null
													: healthLink && healthLink.groupOptions
											}
											disabled={queryGroupNumber ? true : false}
											onChange={handleChangeGroup}
											style={{
												minWidth: 200,
												textAlign: "left",
												overflowY: "visible"
											}}
											MenuProps={{
												style: { zIndex: 35960 }
											}}
											inputProps={{ "aria-label": "Without label" }}
										>
											<MenuItem value="" disabled>
												Select Group
											</MenuItem>
											{groupOptions.map((option: any) => (
												<MenuItem key={option.value} value={option.value}>
													{option.label}
													{/* {console.log(option)} */}
												</MenuItem>
											))}
										</Select>
										{validation && validation.group_number ? (
											<div className="details">
												<span className="select-validation-text2">
													{validation.group_number}
												</span>
											</div>
										) : null}
									</Grid>
								</Grid>
							</Grid>
							<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
								<Grid container spacing={1}>
									<Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
										<div className="label-text" id="label-text">
											Display Text:
										</div>
									</Grid>
									<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
										<TextField
											className="text-field"
											name="text"
											id="member-id"
											style={{
												minWidth: 200,
												//marginLeft: "0.5rem",
												marginTop: "-0.5rem"
											}}
											value={healthLink && healthLink.text}
											onChange={handleLinkChange}
											helperText={validation && validation.text ? validation.text : null}
										/>
									</Grid>
								</Grid>
							</Grid>
							<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
								<Grid container spacing={1}>
									<Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
										<div className="label-text" id="label-text">
											Category:
										</div>
									</Grid>
									<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
										<Select
											id="category-options-id"
											name="country"
											value={healthLink && healthLink.category}
											onChange={handleChange}
											style={{
												minWidth: 200,
												textAlign: "left",
												overflowY: "visible"
											}}
											MenuProps={{
												style: { zIndex: 35960 }
											}}
											inputProps={{ "aria-label": "Without label" }}
										>
											<MenuItem value="" disabled>
												Select Category
											</MenuItem>
											{categoryOptions.current.map((option: any) => (
												<MenuItem key={option.value} value={option.value}>
													{option.label}
												</MenuItem>
											))}
										</Select>
										{validation && validation.category ? (
											<div className="details">
												<span className="select-validation-text2">{validation.category}</span>
											</div>
										) : null}
									</Grid>
								</Grid>
							</Grid>
							<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
								<Grid container spacing={1}>
									<Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
										<div className="label-text" id="label-text">
											URL:
										</div>
									</Grid>
									<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
										<TextField
											className="text-field"
											name="URL"
											id="url-id"
											style={{
												minWidth: 200,
												marginTop: "-0.5rem"
											}}
											value={healthLink && healthLink.URL}
											onChange={handleLinkChange}
											helperText={validation && validation.URL ? validation.URL : null}
										/>
									</Grid>
								</Grid>
							</Grid>
							<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
								<Grid container spacing={1}>
									<Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
										<div className="label-text" id="label-text">
											Description:
										</div>
									</Grid>
									<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
										<TextField
											className="text-field margin-top-2px"
											name="description"
											id="description-id"
											multiline
											rows={5}
											variant="outlined"
											style={{
												minWidth: 200,
												marginTop: "-0.5rem"
											}}
											value={healthLink && healthLink.description}
											onChange={handleLinkChange}
											helperText={
												validation && validation.description ? validation.description : null
											}
										/>
									</Grid>
								</Grid>
							</Grid>
							<div className="check-upload-picture custom-picture" id="check-upload-picture">
								<span>
									<GreenCheckbox onChange={handleCustomImageCheck} name="is_custom_image_enabled" />
								</span>
								<span className="label-text" id="label-text">
									Enable Custom Picture in Health Link
								</span>
							</div>
							<div className="custom-images" id="custom-images">
								{healthLink && healthLink.is_custom_image_enabled ? (
									<>
										<div className="avatar">
											<Card className="health-link-image">
												{healthLink && healthLink.image ? (
													<div className="health-link-img-container">
														<img
															src={
																healthLink &&
																healthLink.image &&
																healthLink.image.uploadDate
																	? generateImageDialogLink(healthLink.image)
																	: URL.createObjectURL(healthLink.image)
															}
															height="100%"
															width="100%"
															alt=""
														/>
													</div>
												) : (
													<div className="health-link-img-container">
														<img src={sampleImage} height="100%" width="100%" alt="" />
													</div>
												)}
											</Card>
										</div>
										<div className="upload-images">
											<span className="icon-container">
												<input
													accept="image/*"
													className="image-upload-input"
													id="image-upload-input"
													multiple
													type="file"
													onChange={handleFileChange}
												/>
												<label htmlFor="image-upload-input">
													<Button
														className="button-green "
														variant="contained"
														aria-label="upload image"
														component="span"
													>
														<span className="button-label-with-icon">Change Picture</span>
														<span>
															<PhotoCamera className="button-icon" />
														</span>
													</Button>
													{validation &&
													validation.image !== undefined &&
													validation.image ? (
														<div className="details" style={{ paddingLeft: "12.7" }}>
															<span className="select-validation-text3">
																{validation.image}
															</span>
														</div>
													) : null}
												</label>
											</span>
										</div>
									</>
								) : null}
							</div>
						</Paper>
					</Grid>
				</>
			) : null}
		</div>
	);
};

export default EditHealthLinks;
